import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { extractAWCDataFromEvent } from '@atlassian/analytics-bridge/extract-awc-data-from-event';
import { NoticeType } from '@atlassian/proforma-common-core/notice-types';
import { ApiErrorType } from '@atlassian/proforma-common-core/api-error-type';
import type { ErrorUtils } from '@atlassian/proforma-common-core/error-utils';
import { ActionSubject } from '@atlassian/proforma-utils/analytics/events';

// Seems there is a linting bug, so adding these disables for "no-shadow" rule. Can't
// find why these would violate from manual analysis and AI analysis from 2 different models (Claude Haiku 4.5, Gemini 2.5 Pro (thinking)).

export type ResponseType = 'json' | 'blob' | 'text' | 'none';

// oxlint-disable-next-line no-shadow
export enum AcceptedResponseType {
	Json = 'json',
	Blob = 'blob',
}

/**
 * Local types for constructing ApiError, matching the definition in proforma-common-core
 * These are not exported from the package, so we define them here.
 */
// oxlint-disable-next-line no-shadow
enum ErrorContext {
	I18nAccessPathKey = 'err.access.path',
	I18nEntityConnectionKey = 'err.entity.connection',
	I18nEntityFormKey = 'err.entity.form',
	I18nEntityIssueKey = 'err.entity.issue',
	I18nEntityIssueCreateKey = 'err.entity.issue.create',
	I18nEntityPreferredFormKey = 'err.entity.preferredform',
	I18nEntityProjectKey = 'err.entity.project',
	I18nEntityRequestTypeKey = 'err.entity.requesttype',
	I18nEntityServiceDeskKey = 'err.entity.servicedesk',
	I18nJwtParseKey = 'err.jwt.parse',
	I18nJwtSignatureKey = 'err.jwt.signed',
	I18nJwtVerifyKey = 'err.jwt.verify',
	I18nInternalServerError = 'err.server.internal',
	I18nUnknownContextError = 'err.context.unknown',
}
interface ApiError {
	error: ApiErrorType;
	isClientFetchError?: boolean;
	traceId?: string;
	status?: number;
	requestName?: string;
}
interface ClientApiError extends ApiError {
	error: ApiErrorType.client;
}
interface ClientWithDetailsApiError extends ApiError {
	error: ApiErrorType.clientWithDetails;
	context: ErrorContext;
	details: string;
}
interface InvalidJsonApiError extends ApiError {
	error: ApiErrorType.json;
	details: string;
}
interface JwtApiError extends ApiError {
	error: ApiErrorType.jwt;
	details: string;
}
interface UnknownApiError extends ApiError {
	error: ApiErrorType.unknown;
	message: string;
	status: number;
	responseJson: Record<string, unknown>;
}

/**
 * Local function to convert a string to ErrorContext, matching proforma-common-core implementation
 */
function ErrorContextOf(context?: string): ErrorContext {
	switch (context) {
		case ErrorContext.I18nAccessPathKey:
			return ErrorContext.I18nAccessPathKey;
		case ErrorContext.I18nEntityConnectionKey:
			return ErrorContext.I18nEntityConnectionKey;
		case ErrorContext.I18nEntityFormKey:
			return ErrorContext.I18nEntityFormKey;
		case ErrorContext.I18nEntityIssueKey:
			return ErrorContext.I18nEntityIssueKey;
		case ErrorContext.I18nEntityIssueCreateKey:
			return ErrorContext.I18nEntityIssueCreateKey;
		case ErrorContext.I18nEntityPreferredFormKey:
			return ErrorContext.I18nEntityPreferredFormKey;
		case ErrorContext.I18nEntityProjectKey:
			return ErrorContext.I18nEntityProjectKey;
		case ErrorContext.I18nEntityRequestTypeKey:
			return ErrorContext.I18nEntityRequestTypeKey;
		case ErrorContext.I18nEntityServiceDeskKey:
			return ErrorContext.I18nEntityServiceDeskKey;
		case ErrorContext.I18nJwtParseKey:
			return ErrorContext.I18nJwtParseKey;
		case ErrorContext.I18nJwtSignatureKey:
			return ErrorContext.I18nJwtSignatureKey;
		case ErrorContext.I18nJwtVerifyKey:
			return ErrorContext.I18nJwtVerifyKey;
		case ErrorContext.I18nInternalServerError:
			return ErrorContext.I18nInternalServerError;
		default:
			return ErrorContext.I18nUnknownContextError;
	}
}

/**
 * Converts HeadersInit to a Record<string, string> for safe assignment
 */
function toHeadersRecord(headers: HeadersInit): Record<string, string> {
	if (headers instanceof Headers) {
		const record: Record<string, string> = {};
		headers.forEach((value, key) => {
			record[key] = value;
		});
		return record;
	}
	if (Array.isArray(headers)) {
		const record: Record<string, string> = {};
		headers.forEach(([key, value]) => {
			record[key] = value;
		});
		return record;
	}
	return headers;
}

/**
 * Constructs an API error object.
 * This is a local implementation matching the original from proforma-common-core/ApiError.ts
 * since constructApiError is not exported from proforma-common-core package.
 */
function constructApiError(
	error: string,
	status: number,
	responseJson: Record<string, unknown>,
	requestName?: string,
	context?: string,
	contextId?: string,
	details?: string,
	isClientFetchError?: boolean,
	traceId?: string,
): ApiError {
	switch (error) {
		case ApiErrorType.client:
			return {
				isClientFetchError,
				traceId,
				status,
				requestName,
				error: ApiErrorType.client,
			} as ClientApiError;
		case ApiErrorType.clientWithDetails:
			return {
				isClientFetchError,
				traceId,
				status,
				requestName,
				error: ApiErrorType.clientWithDetails,
				context: ErrorContextOf(context),
				details: details || '',
			} as ClientWithDetailsApiError;
		case ApiErrorType.json:
			return {
				isClientFetchError,
				traceId,
				status,
				requestName,
				error: ApiErrorType.json,
				details: details || '',
			} as InvalidJsonApiError;
		case ApiErrorType.jwt:
			return {
				isClientFetchError,
				traceId,
				status,
				requestName,
				error: ApiErrorType.jwt,
				details: details || '',
			} as JwtApiError;
		default:
			return {
				isClientFetchError,
				traceId,
				requestName,
				status,
				responseJson,
				error: ApiErrorType.unknown,
				message: error,
			} as UnknownApiError;
	}
}
function getAnalyticsSource(createAnalyticsEvent: CreateUIAnalyticsEvent): string {
	try {
		const analyticsEvent = createAnalyticsEvent({});
		const analyticsData = extractAWCDataFromEvent(analyticsEvent);
		const source = analyticsData?.payload?.source as unknown;
		return typeof source === 'string' ? source : 'unknown';
	} catch (e) {
		return 'unknown';
	}
}

function toApiError(
	status: number,
	responseJson: Record<string, unknown>,
	isClientFetchError: boolean,
	requestName?: string,
	traceId?: string,
): ApiError {
	if (Object.keys(responseJson).length === 0) {
		return constructApiError(
			`Unexpected API reply (HTTP/${status})`,
			status,
			responseJson,
			requestName,
		);
	}
	const error = String(responseJson.error) || `Unexpected API reply (HTTP/${status})`;
	const context = String(responseJson.context ?? '');
	const contextId = String(responseJson.contextId ?? '');
	const details = String(responseJson.details ?? '');
	return constructApiError(
		error,
		status,
		responseJson,
		requestName,
		context,
		contextId,
		details,
		isClientFetchError,
		traceId,
	);
}

/**
 * Fetches data from an API with analytics tracking and error handling.
 * This function provides the same analytics and error handling as ApiUtil.fetchApi
 * but is decoupled from the ApiUtil class.
 *
 * @param input - The resource to fetch (URL or Request object), passed directly to fetch
 * @param init - The request initialization options, passed directly to fetch
 * @param createAnalyticsEvent - The analytics event creator from useAnalyticsEvents hook
 * @param errorUtils - The error utilities from usePfErrorUtils hook
 * @param responseType - The expected response type (default: 'json')
 * @param requestName - Optional name for the request (used in error reporting)
 * @returns Promise that resolves with the response data or rejects with an ApiError
 */
export async function proformaGasAnalyticsFetch<T = unknown>(
	input: Parameters<typeof fetch>[0],
	init: Parameters<typeof fetch>[1],
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	errorUtils: ErrorUtils,
	responseType: ResponseType = 'json',
	requestName?: string,
): Promise<T> {
	const analyticsSource = getAnalyticsSource(createAnalyticsEvent);

	// Merge the analytics headers with any existing headers from init
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		'X-Experimental-Experience': analyticsSource,
		...(responseType === AcceptedResponseType.Json ? { Accept: 'application/json' } : {}),
		...(init?.headers ? toHeadersRecord(init.headers) : {}),
	};

	const fetchInit: RequestInit = {
		credentials: init?.credentials ?? 'same-origin',
		...(init ? { ...init, headers: undefined } : {}),
		headers,
	};

	// Extract path and method for error reporting
	let path: string;
	if (typeof input === 'string') {
		path = input;
	} else if (input instanceof URL) {
		path = input.toString();
	} else {
		path = 'unknown';
	}
	const method = init?.method ?? 'GET';

	return fetch(input, fetchInit)
		.catch((error: unknown) => {
			const fetchError = error instanceof Error ? error : new Error(String(error));
			const message = `No response from API: ${fetchError.message || ''}`;
			const apiError = toApiError(0, { error: message }, true, requestName);
			errorUtils.reportError(fetchError, {
				actionSubject: ActionSubject.PROFORMA_API,
				id: 'fetchApi',
				attributes: {
					path,
					method,
					responseType,
					requestName,
					apiError: JSON.stringify(apiError),
					isClientFetchError: true,
				},
			});
			return Promise.reject(apiError);
		})
		.then((response) => {
			let errorObj: Error = new Error(response.statusText);
			if (!response.ok) {
				const traceId =
					response.headers.get('x-trace-id') ?? response.headers.get('atl-traceid') ?? undefined;
				let apiErrorPromise;
				try {
					// Try to parse response error details
					const responseJsonApiError =
						responseType !== 'json'
							? undefined
							: response
									.json()
									.then((responseJson: Record<string, unknown>) =>
										toApiError(response.status, responseJson, false, requestName, traceId),
									)
									.catch((error: unknown) => {
										const jsonError = error instanceof Error ? error : new Error(String(error));
										const message = `Failed to parse error response as JSON from API: ${
											jsonError.message || ''
										}`;
										errorObj = jsonError;
										return toApiError(
											response.status,
											{ error: message },
											false,
											requestName,
											traceId,
										);
									});
					if (response.status === 401) {
						errorUtils.notifyUser(NoticeType.ErrorApiUnauthorised).catch(() => {
							// Notification error handling is not critical
						});
					}
					if (response.status === 403) {
						errorUtils.notifyUser(NoticeType.ErrorApiMissingPermissions).catch(() => {
							// Notification error handling is not critical
						});
					}
					if (responseJsonApiError !== undefined) {
						apiErrorPromise = responseJsonApiError.then((apiError) => Promise.reject(apiError));
					} else {
						// Create error object with error response body
						apiErrorPromise = Promise.reject(
							toApiError(
								response.status,
								{ error: response.body || '' },
								false,
								requestName,
								traceId,
							),
						);
					}
				} catch (error) {
					// Nothing blocking this lint issue being fixed, but leaving as ignored for now to lower surface area for potential
					// regressions between this and the platform ApiUtil implementation, and this entire function will be gone once
					// moving to UFO anyway.
					// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
					const message = `Unexpected response from API: ${response.body || ''}`;
					errorObj = error as Error;
					apiErrorPromise = Promise.reject(
						toApiError(response.status, { error: message }, false, requestName, traceId),
					);
				}

				apiErrorPromise.catch((apiError) => {
					errorUtils.reportError(errorObj, {
						actionSubject: ActionSubject.PROFORMA_API,
						id: 'fetchApi',
						attributes: {
							method,
							path,
							requestName,
							responseType,
							traceId,
							apiError: JSON.stringify(apiError),
							isClientFetchError: false,
							status: response.status,
							statusGroup: `${Math.floor(response.status / 100)}xx`,
						},
					});
				});

				return apiErrorPromise;
			}
			if (response.status === 204) {
				return '' as T;
			}
			switch (responseType) {
				case 'json':
					return response.json() as Promise<T>;
				case 'blob':
					return response.blob() as Promise<T>;
				case 'text':
					return response.text() as Promise<T>;
				case 'none':
					return '' as T;
				default:
					return response as T;
			}
		}) as Promise<T>;
}
